import React, { useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@material-ui/core";
import PublishOutlinedIcon from "@material-ui/icons/PublishOutlined";
import useStyles from "./styles";
import CrmService, { apiError } from "./crmService";
import { IMPORT_FIELDS, autoMap, downloadImportTemplate, readSheet } from "./crmExcel";
import { EXCEL_MAX_FILE_SIZE_MB, getExcelSizeError } from "../../../utils/uploadLimits";

const MAX_ROWS = 2000;

/** Upload → map columns → preview → import → per-row result. */
export default function ImportDialog({ open, onClose, onImported }) {
  const classes = useStyles();
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [mapping, setMapping] = useState([]);
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep("upload");
    setFile(null);
    setSheet(null);
    setMapping([]);
    setAllowDuplicates(false);
    setError("");
    setResult(null);
  };
  const close = () => {
    if (busy) return;
    if (result?.created) onImported && onImported(result);
    reset();
    onClose();
  };

  const pickFile = async (f) => {
    setError("");
    if (!f) return;
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) return setError("Choose an Excel (.xlsx, .xls) or CSV file.");
    const sizeError = getExcelSizeError(f);
    if (sizeError) return setError(sizeError);
    setBusy(true);
    try {
      const data = await readSheet(f);
      if (!data.rows.length) throw new Error("The file has no rows under the header.");
      if (data.rows.length > MAX_ROWS) throw new Error(`The file has ${data.rows.length} rows. Import up to ${MAX_ROWS} at a time.`);
      setFile(f);
      setSheet(data);
      setMapping(autoMap(data.headers));
      setStep("map");
    } catch (err) {
      setError(err.message || "Could not read the file.");
    } finally {
      setBusy(false);
    }
  };

  const mappedRows = () =>
    sheet.rows.map((r) => {
      const row = { __row: r.rowNo };
      mapping.forEach((key, i) => {
        if (key && r.cells[i] !== "") row[key] = r.cells[i];
      });
      return row;
    });

  const hasMobile = mapping.includes("mobile");
  const hasName = mapping.includes("parentName") || mapping.includes("childName");

  const runImport = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await CrmService.importRows(mappedRows(), allowDuplicates);
      setResult(res);
      setStep("done");
    } catch (err) {
      setError(apiError(err, "Import failed.").message);
    } finally {
      setBusy(false);
    }
  };

  const preview = sheet ? mappedRows().slice(0, 5) : [];
  const mappedKeys = IMPORT_FIELDS.filter((f) => mapping.includes(f.key));

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle disableTypography>
        <div style={{ fontSize: 20, fontWeight: 600 }}>Import enquiries</div>
        <div className={classes.muted}>
          {step === "upload" && "Upload a cold-call list or any enquiry sheet. You'll match its columns next."}
          {step === "map" && `${file?.name} · ${sheet?.rows.length} rows. Match each column to a CRM field.`}
          {step === "done" && result?.message}
        </div>
      </DialogTitle>
      <DialogContent dividers>
        {step === "upload" && (
          <div style={{ display: "grid", gap: 16, justifyItems: "start" }}>
            <Button variant="contained" color="primary" component="label" startIcon={busy ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <PublishOutlinedIcon />} disabled={busy}>
              Choose file
              <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={(e) => pickFile(e.target.files?.[0])} />
            </Button>
            <div className={classes.muted}>
              .xlsx, .xls or .csv, up to {EXCEL_MAX_FILE_SIZE_MB} MB and {MAX_ROWS} rows. The first row must be the column names.
              Only Mobile is required; rows need a parent or child name.
            </div>
            <Button variant="text" style={{ color: "#0b5cc2", padding: 0 }} onClick={downloadImportTemplate}>
              Download template
            </Button>
          </div>
        )}

        {step === "map" && sheet && (
          <>
            <div className={classes.tableScroll}>
              <table className={classes.table} style={{ minWidth: 520 }}>
                <thead>
                  <tr>
                    <th>Column in your file</th>
                    <th>Example</th>
                    <th>CRM field</th>
                  </tr>
                </thead>
                <tbody>
                  {sheet.headers.map((h, i) => (
                    <tr key={`${h}-${i}`} style={{ cursor: "default" }}>
                      <td>{h}</td>
                      <td className={classes.ellipsis} style={{ color: "#555" }}>
                        {sheet.rows.find((r) => r.cells[i])?.cells[i] || "-"}
                      </td>
                      <td style={{ width: 240 }}>
                        <TextField
                          select
                          value={mapping[i] || ""}
                          onChange={(e) => {
                            const next = [...mapping];
                            const key = e.target.value;
                            if (key) next.forEach((k, j) => k === key && (next[j] = ""));
                            next[i] = key;
                            setMapping(next);
                          }}
                          variant="outlined"
                          size="small"
                          fullWidth
                          SelectProps={{ displayEmpty: true }}
                        >
                          <MenuItem value="">
                            <em>Don't import</em>
                          </MenuItem>
                          {IMPORT_FIELDS.map((f) => (
                            <MenuItem key={f.key} value={f.key}>
                              {f.label}
                              {f.required ? " *" : ""}
                            </MenuItem>
                          ))}
                        </TextField>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {mappedKeys.length > 0 && (
              <>
                <h3 className={classes.sectionTitle} style={{ marginTop: 22 }}>
                  Preview (first {preview.length} rows)
                </h3>
                <div className={classes.tableScroll}>
                  <table className={classes.table} style={{ minWidth: 520 }}>
                    <thead>
                      <tr>
                        <th>Row</th>
                        {mappedKeys.map((f) => (
                          <th key={f.key}>{f.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((r) => (
                        <tr key={r.__row} style={{ cursor: "default" }}>
                          <td>{r.__row}</td>
                          {mappedKeys.map((f) => (
                            <td key={f.key} className={classes.ellipsis}>
                              {r[f.key] || ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <FormControlLabel
              style={{ marginTop: 12 }}
              control={<Checkbox checked={allowDuplicates} onChange={(e) => setAllowDuplicates(e.target.checked)} color="default" />}
              label="Also import mobiles that are already in the CRM"
            />
            {!hasMobile && <div className={classes.error}>Match a column to Mobile to continue.</div>}
            {hasMobile && !hasName && <div className={classes.error}>Match a column to Parent or Child name to continue.</div>}
          </>
        )}

        {step === "done" && result && (
          <>
            <div className={classes.tiles} style={{ marginBottom: 16 }}>
              {[
                ["Imported", result.created, "#1f8a5b"],
                ["Skipped (already in CRM)", result.skipped, "#c27a00"],
                ["Failed", result.failed, "#b3261e"],
              ].map(([label, value, color]) => (
                <div key={label} className={`${classes.tile} ${classes.tileStatic}`}>
                  <div className={classes.tileLabel}>
                    <span className={classes.dot} style={{ background: color }} />
                    {label}
                  </div>
                  <div className={classes.tileValue}>{value}</div>
                </div>
              ))}
            </div>
            {result.results.some((r) => r.status !== "created") && (
              <div className={classes.tableScroll}>
                <table className={classes.table} style={{ minWidth: 480 }}>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Result</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results
                      .filter((r) => r.status !== "created")
                      .map((r) => (
                        <tr key={r.row} style={{ cursor: "default" }}>
                          <td>{r.row}</td>
                          <td>{r.status === "failed" ? "Failed" : "Skipped"}</td>
                          <td>{r.message}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {error && <div className={classes.error} style={{ marginTop: 12 }}>{error}</div>}
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px" }}>
        {step === "map" && (
          <Button variant="outlined" onClick={reset} disabled={busy}>
            Choose another file
          </Button>
        )}
        <Button variant={step === "done" ? "contained" : "outlined"} color={step === "done" ? "primary" : "default"} onClick={close} disabled={busy}>
          {step === "done" ? "Done" : "Cancel"}
        </Button>
        {step === "map" && (
          <Button
            variant="contained"
            color="primary"
            onClick={runImport}
            disabled={busy || !hasMobile || !hasName}
            startIcon={busy ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
          >
            Import {sheet?.rows.length} rows
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
